/**
 * ResourceCardRenderer - компонент для рендерингу вкладених карточок ресурсів
 *
 * Відповідає за:
 * - Відображення активних карточок у drawer/modal режимах
 * - Управління стеком карточок
 * - Передачу displayMode з карточки в ResourceDetail
 * - Інтеграція з ResourceDetail компонентом (який сам керує Drawer/Modal)
 *
 * ВАЖЛИВО:
 * - Цей компонент рендерить ТІЛЬКИ карточки з displayMode === 'drawer' або 'modal'
 * - Карточки з displayMode === 'page' не додаються в стек (відбувається push навігація)
 * - displayMode береться з IActiveResourceCard.displayMode
 *
 * ОПТИМІЗАЦІЯ:
 * - Використовуються стабільні слоти замість динамічного масиву
 * - Кожен слот має власний MobX observable стан для уникнення перерендеру всіх карток
 * - Заповнення/очищення слоту не викликає перерендеру інших слотів
 */

import { makeAutoObservable, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo } from 'react'

import type { IActiveResourceCard } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { SubscribeToResourceCardsTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { ResourceDetail } from '@/Core/UI/CrudResource'

/** Кількість зарезервованих слотів для карток */
const MAX_CARD_SLOTS = 100

/**
 * Observable стан слоту картки
 * Кожен слот незалежно відстежує свій стан, що дозволяє уникнути
 * перерендеру всіх карток при зміні однієї
 */
class CardSlotState {
    card: IActiveResourceCard | null = null

    constructor() {
        makeAutoObservable(this)
    }

    setCard(card: IActiveResourceCard | null) {
        this.card = card
    }

    get isActive(): boolean {
        return this.card !== null && 
            (this.card.displayMode === 'drawer' || this.card.displayMode === 'modal')
    }
}

/**
 * Менеджер слотів для карток
 * Singleton, який зберігає стабільний масив слотів та розподіляє картки по них
 */
class CardSlotsManager {
    private static instance: CardSlotsManager
    /** Стабільний масив слотів, який ніколи не змінюється */
    readonly slots: CardSlotState[]
    /** Мапа для швидкого пошуку слоту за ID картки */
    private cardToSlotIndex: Map<string, number> = new Map()

    private constructor() {
        // Створюємо стабільний масив слотів один раз
        this.slots = Array.from({ length: MAX_CARD_SLOTS }, () => new CardSlotState())
    }

    static getInstance(): CardSlotsManager {
        if (!CardSlotsManager.instance) {
            CardSlotsManager.instance = new CardSlotsManager()
        }
        return CardSlotsManager.instance
    }

    /**
     * Оновлює слоти відповідно до поточного списку активних карток
     * Зберігає стабільність слотів для існуючих карток
     */
    updateCards(cards: IActiveResourceCard[]): void {
        runInAction(() => {
            // Фільтруємо тільки drawer/modal картки
            const activeCards = cards.filter(
                card => card.displayMode === 'drawer' || card.displayMode === 'modal'
            )

            // Створюємо Set з ID активних карток для швидкого пошуку
            const activeCardIds = new Set(activeCards.map(card => card.id))

            // Очищуємо слоти карток, яких більше немає
            for (const [cardId, slotIndex] of this.cardToSlotIndex.entries()) {
                if (!activeCardIds.has(cardId)) {
                    this.slots[slotIndex]!.setCard(null)
                    this.cardToSlotIndex.delete(cardId)
                }
            }

            // Оновлюємо існуючі або призначаємо нові слоти
            for (const card of activeCards) {
                const existingSlotIndex = this.cardToSlotIndex.get(card.id)

                if (existingSlotIndex !== undefined) {
                    // Картка вже має слот - НЕ оновлюємо, якщо ID той самий
                    // Це запобігає зайвим перерендерам
                    const currentCard = this.slots[existingSlotIndex]!.card
                    if (currentCard?.id !== card.id) {
                        this.slots[existingSlotIndex]!.setCard(card)
                    }
                    // Інакше - нічого не робимо, картка та ж сама
                } else {
                    // Шукаємо вільний слот
                    const freeSlotIndex = this.findFreeSlot()
                    if (freeSlotIndex !== -1) {
                        this.slots[freeSlotIndex]!.setCard(card)
                        this.cardToSlotIndex.set(card.id, freeSlotIndex)
                    } else {
                        console.warn('[ResourceCardRenderer] Немає вільних слотів для картки:', card.id)
                    }
                }
            }
        })
    }

    /**
     * Знаходить перший вільний слот
     */
    private findFreeSlot(): number {
        for (let i = 0; i < this.slots.length; i++) {
            if (this.slots[i]!.card === null) {
                return i
            }
        }
        return -1
    }
}

/**
 * Компонент для рендерингу одного слоту
 * Підписується тільки на зміни свого слоту через MobX observer
 */
const CardSlot: React.FC<{ slot: CardSlotState }> = observer(({ slot }) => {
    // Якщо слот порожній або картка не drawer/modal - нічого не рендеримо
    if (!slot.isActive || !slot.card) {
        return null
    }

    const card = slot.card

    // Використовуємо displayMode з карточки
    const displayMode = card.displayMode || 'drawer'

    // Формуємо пропси безпечно
    const detailProps: any = {
        resource: card.resource,
        mode: card.mode,
        displayMode,
        listCrudContext: card.listCrudContext,
        ...card.props,
    }

    if (card.resourceId !== undefined) {
        detailProps.itemId = card.resourceId
    }

    if (card.onSave) {
        detailProps.onSave = card.onSave
    }

    if (card.onClose) {
        detailProps.onClose = card.onClose
    }

    if (card.onModeChange) {
        detailProps.onModeChange = card.onModeChange
    }

    return <ResourceDetail {...detailProps} />
})

/**
 * Основний компонент для рендерингу всіх активних карточок
 * Рендерить стабільний масив слотів, кожен з яких самостійно вирішує чи показувати картку
 */
export const ResourceCardRenderer: React.FC = observer(() => {
    // Отримуємо singleton менеджера слотів
    const slotsManager = useMemo(() => CardSlotsManager.getInstance(), [])

    useEffect(() => {
        // Підписуємося на зміни активних карточок
        const unsubscribe = SubscribeToResourceCardsTask((cards) => {
            slotsManager.updateCards(cards)
        })

        return unsubscribe
    }, [slotsManager])
    
    // Рендеримо всі слоти - це відбувається тільки один раз при монтуванні
    // Кожен слот сам вирішує чи показувати свій контент
    return (
        <>
            {slotsManager.slots.map((slot, index) => (
                <CardSlot key={index} slot={slot} />
            ))}
        </>
    )
})

export default ResourceCardRenderer
